import { getSubdomainData } from '@apparel/apparel/src/lib/utils';
import { responseErrorMessage } from '@apparel/core/api';
import { HTTPCodes } from '@apparel/core/api/HTTPCodes';
import { AuthService } from '@apparel/core/auth';
import { call } from '@apparel/core/utils/functions';
import { useLoading } from '@apparel/react/hooks/useLoading';
import { useService } from '@apparel/react/hooks/useService';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grow from '@material-ui/core/Grow';
import Slide from '@material-ui/core/Slide';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Form, Formik, FormikHelpers } from 'formik';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import * as Yup from 'yup';
import { CartEmitter, CartEvents } from '../../../cart/CartEmitter';
import { AttributionService } from '../../../lib/AttributionService';
import { GoogleAnalyticsService } from '../../../lib/GoogleAnalyticService';
import { GoogleTagService } from '../../../lib/GoogleTag';
import { GOOGLE_ANALYTIC_SERVICE, GTag } from '../../../lib/utils';
import { AuthViewType } from '../AuthButtonProps';
import { useNewAuthDialog } from '../NewAuthDialog/NewAuthDialogContext';
import { FormError } from './FormError';
import { ForgotPassword } from './Forms/ForgotPassword';
import { SignIn } from './Forms/SignIn';
import { SignUp } from './Forms/SignUp';
import { NewAuthFormContext, NewAuthFormState } from './NewAuthFormContext';
import { NewAuthFormProvider } from './NewAuthFormProvider';
import { ViewField } from './ViewField';
const useStyles = makeStyles(
    () => ({
        loading: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            position: 'absolute',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, .75)',
        },
    }),
    {
        name: 'NewAuthForm',
    },
);

interface NewAuthFormProps {
    view?: AuthViewType;
    title?: string;
    cta?: string;
}

export function NewAuthForm({ view, title, cta }: NewAuthFormProps) {
    const classes = useStyles();
    const NewAuthFormRef = useRef<NewAuthFormState>(null);
    const auth$ = useService(AuthService);
    const newDialog$ = useNewAuthDialog();
    const referral_code = localStorage.getItem('referral');

    useEffect(() => {
        AttributionService.get();
    }, []);

    const initialValues = useMemo(
        () => ({
            view: 'signIn',
            error: null,
            identifier: auth$.lastIdentifier || '',
            password: '',
            first_name: '',
            full_name: '',
            last_name: '',
            email: '',
            phone: '',
            country_code: '',
            country: {
                id: 236,
                name: 'United States',
            },
            state: '',
            city_name: '',
            sport: '',
            sport_other: '',
            source: '',
            source_other: '',
            what_role: '',
            what_role_other: '',
            authorized_to_order: '',
            instagram_handle: '',
            ...AttributionService.get(),
        }),
        [],
    );

    const [loading, { setState: setLoading }] = useLoading();

    const handleMainSubmit = useCallback(async (values, formik: FormikHelpers<any>) => {
        const { data } = await auth$.checkEmail(values.identifier);

        if (!data.exist) {
            formik.setFieldError('identifier', "Account doesn't exist.");
        } else {
            auth$.lastIdentifier = values.identifier;
            NewAuthFormRef.current.setView('signIn');
        }
    }, []);

    const handleSignInSubmit = useCallback(
        async (values, formik: FormikHelpers<any>) => {
            try {
                CartEmitter.on(CartEvents.ITEMS_UPLOADED, () => {
                    call(newDialog$.onEnd);
                });

                await auth$.authenticateWithIdentifierAndPassword(values.identifier, values.password, {}, false);
            } catch (e) {
                const { response } = e;
                formik.setFieldError(
                    'error',
                    responseErrorMessage(response, {
                        [HTTPCodes.HTTP_UNAUTHORIZED]: 'Invalid credentials!',
                    }),
                );
            }
        },
        [newDialog$.onEnd],
    );

    const getSignUpProperties = useCallback((values) => {
        const phoneValues: Record<string, any> = {};
        const subdomainData = getSubdomainData();

        if (values.phone) {
            phoneValues.phone = values.phone;
            phoneValues.phone_country_calling_alpha2 = values.country_code;
            phoneValues.phone_strategy = 'full';
        }

        const attribution = AttributionService.getForSignup();

        return {
            first_name: values.first_name,
            last_name: values.last_name,
            email: values.email,
            referral_code,
            password: values.password,
            password_confirmation: values.password,
            country_id: values.country.id,
            state: values.state,
            city_name: values.city_name,
            sport: [values.sport === 'Other' ? values.sport_other : values.sport],
            source: values.source === 'Other' ? values.source_other : values.source,
            what_role: values.what_role === 'Other' ? values.what_role_other : values.what_role,
            authorized_to_order: values.authorized_to_order === true || values.authorized_to_order === 'true',
            instagram_handle: values.instagram_handle,
            wholesaler_id: subdomainData?.wholesaler_id,
            utm_source: values.utm_source || attribution.utm_source || undefined,
            utm_medium: values.utm_medium || attribution.utm_medium || undefined,
            utm_campaign: values.utm_campaign || attribution.utm_campaign || undefined,
            gclid: values.gclid || attribution.gclid || undefined,
            fbclid: values.fbclid || attribution.fbclid || undefined,
            ttclid: values.ttclid || attribution.ttclid || undefined,
            msclkid: values.msclkid || attribution.msclkid || undefined,
            ...phoneValues,
        };
    }, []);

    const handleSignUpSubmit = useCallback(
        async (values, formik: FormikHelpers<any>) => {
            try {
                const signUpProperties = await getSignUpProperties(values);

                await auth$.createUser(signUpProperties);
                CartEmitter.on(CartEvents.ITEMS_UPLOADED, () => {
                    if (newDialog$.onEnd) {
                        call(newDialog$.onEnd);
                    }
                });
                localStorage.removeItem('referral');
                AttributionService.clear();
                GoogleTagService.reportConversion(GTag.SignUp);
                GoogleAnalyticsService.reportConversion(GOOGLE_ANALYTIC_SERVICE.NEW_FORM_SUBMISSION);
            } catch (e) {
                const { response } = e;
                formik.setFieldError('error', responseErrorMessage(response));
            }
        },
        [newDialog$.onEnd],
    );

    const handleForgotPasswordSubmit = useCallback(async (values, formik: FormikHelpers<any>) => {
        try {
            console.log(values);
        } catch (e) {
            const { response } = e;
            formik.setFieldError('error', responseErrorMessage(response));
        }
    }, []);

    const handleSubmit = useCallback(
        async (values, formik) => {
            setLoading(true);

            try {
                switch (values.view) {
                    case 'signIn':
                        await handleSignInSubmit(values, formik);
                        break;
                    case 'signUp':
                        await handleSignUpSubmit(values, formik);
                        break;
                    case 'forgotPassword':
                        await handleForgotPasswordSubmit(values, formik);
                        break;
                }
            } catch (e) {
                formik.setFieldError('error', 'Sorry, something went wrong there.');
            }

            setLoading(false);
        },
        [setLoading, handleMainSubmit, handleSignInSubmit, handleSignUpSubmit, handleForgotPasswordSubmit],
    );

    return (
        <NewAuthFormProvider view={view} title={title} cta={cta} apiRef={NewAuthFormRef}>
            <NewAuthFormContext.Consumer>
                {({ view }) => (
                    <Formik
                        initialValues={initialValues}
                        onSubmit={handleSubmit}
                        validationSchema={schema[view]}
                        validateOnChange
                    >
                        <Form>
                            <FormError />
                            <ViewField />

                            <Slide in={view === 'signIn'} direction={'left'} unmountOnExit exit={false}>
                                <SignIn />
                            </Slide>

                            <Slide in={view === 'signUp'} direction={'left'} unmountOnExit exit={false}>
                                <SignUp />
                            </Slide>

                            <Slide in={view === 'forgotPassword'} direction={'left'} unmountOnExit exit={false}>
                                <ForgotPassword />
                            </Slide>

                            <Grow in={loading} unmountOnExit>
                                <div className={classes.loading}>
                                    <CircularProgress size={32} thickness={2} />
                                </div>
                            </Grow>
                        </Form>
                    </Formik>
                )}
            </NewAuthFormContext.Consumer>
        </NewAuthFormProvider>
    );
}

const schema: Record<AuthViewType, any> = {
    main: Yup.object().shape({
        identifier: Yup.string().required('A phone or email is required!'),
    }),

    forgotPassword: Yup.object().shape({
        identifier: Yup.string().required('A phone or email is required!'),
    }),

    signIn: Yup.object().shape({
        identifier: Yup.string().required('A phone or email is required!'),
        password: Yup.string().required('Password is required!'),
    }),

    signUp: Yup.object().shape({
        first_name: Yup.string().required('Required Field!').max(255, 'Value is too big!'),
        last_name: Yup.string().required('Required Field!').max(255, 'Value is too big!'),

        email: Yup.string().required('Required Field!').email('Invalid email value!').max(255, 'Value is too big!'),

        country: Yup.object({
            id: Yup.number().required('Required Field!'),
            name: Yup.string().required('Required Field!'),
        }),

        state: Yup.string().required('Required Field!'),

        city_name: Yup.string().required('Required Field!'),

        sport: Yup.string().required('Required Field!'),

        sport_other: Yup.string().when('sport', {
            is: (value) => value === 'Other',
            then: Yup.string().required('Required Field!'),
        }),

        source: Yup.string().required('Required Field!'),

        source_other: Yup.string().when('source', {
            is: (value) => value === 'Other',
            then: Yup.string().required('Required Field!'),
        }),

        what_role: Yup.string().required('Required Field!'),

        what_role_other: Yup.string().when('what_role', {
            is: (value) => value === 'Other',
            then: Yup.string().required('Required Field!'),
        }),

        authorized_to_order: Yup.mixed().oneOf([true, false], 'Required Field!').required('Required Field!'),

        instagram_handle: Yup.string(),

        phone: Yup.number().required('Phone is required').typeError('Phone must be a number'),

        password: Yup.string().required('Required Field!').min(6, 'Password should be at least 6 characters!'),
    }),
};

export default Form;
